// src/main/content/contests/ContestCumulativeChart.js
//
// Line chart showing each top-N employee's cumulative metric value over the
// contest window. Driven by the `/get_contest_timeline` endpoint's
// `{dates, employees: [{id, full_name, cumulative}]}` shape.
//
// We deliberately render only the top 5 employees by final cumulative — with
// 20+ participants the chart becomes spaghetti. Top 5 lines up with the rest
// of the page (podium = top 3, leaderboard table = all) and matches what the
// design inspiration showed.

import React, { useMemo } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import dayjs from "dayjs";
import ShowChartIcon from "@mui/icons-material/ShowChart";

// Stable palette ordered by visual interest — leader (violet, brand color)
// gets the most distinctive line. Mirrors the inspiration screenshots.
export const TIMELINE_COLORS = [
  "#7c3aed", // violet-600
  "#3b82f6", // blue-500
  "#f97316", // orange-500
  "#10b981", // emerald-500
  "#ec4899", // pink-500
  "#f59e0b", // amber-500
  "#06b6d4", // cyan-500
  "#f43f5e", // rose-500
];

const TOP_N = 5;

function formatChartValue(value, format) {
  const v = Number(value);
  if (Number.isNaN(v)) return value;
  if (format === "dollars") {
    return `$${v.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
  }
  if (format === "hours") return `${v.toFixed(1)}h`;
  if (format === "goal_percent") return `${Math.floor(v)}%`;
  // Ratio metrics need both decimals — 1-decimal rounding would collapse
  // nearby rates (0.46 vs 0.52 → "0.5").
  if (format === "rate") return v.toFixed(2);
  return v.toLocaleString("en-US", { maximumFractionDigits: 1 });
}

function ChartTooltip({ active, payload, label, format, lowerIsBetter }) {
  if (!active || !payload?.length) return null;
  // Sort payload so the metric's leader is at the top of the tip. Descending
  // for higher-is-better, ascending for lower-is-better — otherwise a
  // late-clock-ins tooltip would put the worst offender at the top.
  const sorted = [...payload].sort((a, b) => {
    const av = a.value ?? 0;
    const bv = b.value ?? 0;
    return lowerIsBetter ? av - bv : bv - av;
  });
  return (
    <div className="rounded-lg bg-white border border-slate-200 shadow-md px-3 py-2 text-xs">
      <div className="font-medium text-slate-900 mb-1.5">{label}</div>
      <div className="space-y-1">
        {sorted.map((p) => (
          <div key={p.dataKey} className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-1.5">
              <span
                className="w-2 h-2 rounded-full inline-block"
                style={{ backgroundColor: p.color }}
              />
              <span className="text-slate-600">{p.dataKey}</span>
            </div>
            <span className="font-medium text-slate-900">
              {formatChartValue(p.value, format)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ContestCumulativeChart({
  timeline,
  metricFormat,
  metricLabel,
  showStoreTag = false,
  lowerIsBetter = false,
  // Goal metrics: draw a dashed "Goal" line at this value (100 = 100%).
  goalValue = null,
}) {
  // Build the Recharts data array: each row is one date with one column per
  // top-N employee's cumulative value at that date.
  const { rows, legend } = useMemo(() => {
    if (!timeline?.dates?.length || !timeline?.employees?.length) {
      return { rows: [], legend: [] };
    }
    const top = timeline.employees.slice(0, TOP_N);
    const legendItems = top.map((e, i) => {
      // ``e.full_name`` is already pre-formatted by ContestDetails — for
      // employees it ran through formatName, for stores it's the raw user-
      // typed value. Don't re-format here.
      const baseName = e.full_name || `Player ${i + 1}`;
      // For multi-store contests, prefer "Name (Store)" over the index-based
      // "Name (2)" disambiguation below — the store tag carries real meaning
      // for the viewer instead of just disambiguating duplicates.
      const key =
        showStoreTag && e.store_name ? `${baseName} (${e.store_name})` : baseName;
      return {
        id: e.id,
        key,
        color: TIMELINE_COLORS[i % TIMELINE_COLORS.length],
      };
    });
    // Make legend keys unique even if two employees somehow share a display
    // name AND store (rare, but possible — and after the store-tag treatment
    // above, this is the last line of defense).
    const seen = new Map();
    legendItems.forEach((l) => {
      const count = seen.get(l.key) || 0;
      seen.set(l.key, count + 1);
      if (count > 0) l.key = `${l.key} (${count + 1})`;
    });

    const rowsBuilt = timeline.dates.map((d, dateIdx) => {
      const row = { date: dayjs(d).format("MMM D") };
      top.forEach((emp, empIdx) => {
        row[legendItems[empIdx].key] = emp.cumulative[dateIdx] ?? 0;
      });
      return row;
    });

    return { rows: rowsBuilt, legend: legendItems };
  }, [timeline]);

  if (!timeline || !rows.length || !legend.length) return null;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <ShowChartIcon className="text-violet-600" style={{ fontSize: 18 }} />
          <h3 className="text-base font-semibold text-slate-900">
            Cumulative {metricLabel || "Score"} Over Time
          </h3>
        </div>
        <span className="text-xs text-slate-500">
          Top {legend.length} by current ranking
        </span>
      </div>

      <div className="p-3 pt-4">
        {/* Chart proper. Fixed-height container so Recharts has a stable
            box to size into. The legend lives OUTSIDE this div so the
            plot area stays the full 224px regardless of how many lines
            the legend wraps to — Recharts' built-in <Legend> would have
            shrunk the plot area when names wrapped, making the chart feel
            cramped on multi-store contests with long employee names. */}
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={rows} margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
              <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 11, fill: "#94a3b8" }}
                axisLine={{ stroke: "#e2e8f0" }}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 11, fill: "#94a3b8" }}
                axisLine={{ stroke: "#e2e8f0" }}
                tickLine={false}
                tickFormatter={(v) => formatChartValue(v, metricFormat)}
                width={50}
              />
              {goalValue != null && (
                <ReferenceLine
                  y={goalValue}
                  stroke="#94a3b8"
                  strokeDasharray="4 4"
                  ifOverflow="extendDomain"
                  label={{
                    value: "Goal",
                    position: "insideTopRight",
                    fontSize: 11,
                    fill: "#64748b",
                  }}
                />
              )}
              <Tooltip
                cursor={{ stroke: "#cbd5e1", strokeDasharray: "3 3" }}
                content={
                  <ChartTooltip
                    format={metricFormat}
                    lowerIsBetter={lowerIsBetter}
                  />
                }
              />
              {legend.map((l) => (
                <Line
                  key={l.id}
                  type="monotone"
                  dataKey={l.key}
                  stroke={l.color}
                  strokeWidth={2.25}
                  // No per-point dot markers — they overlay the line as small
                  // circles and at typical density read as visual gaps /
                  // chunks rather than markers. Hover still shows an
                  // activeDot so the user can pin down a specific date.
                  dot={false}
                  activeDot={{ r: 5, strokeWidth: 0, fill: l.color }}
                  isAnimationActive
                  animationDuration={500}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Custom legend — wraps freely below the chart instead of stealing
            vertical space from the plot area. Centered, with the same color
            dot + name pattern Recharts would have used. */}
        <div className="mt-3 flex flex-wrap justify-center gap-x-4 gap-y-1.5 text-xs text-slate-600">
          {legend.map((l) => (
            <span key={l.id} className="inline-flex items-center gap-1.5">
              <span
                className="w-2 h-2 rounded-full inline-block shrink-0"
                style={{ backgroundColor: l.color }}
              />
              {l.key}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
